import { useCallback, useEffect, useState } from 'react';
import { api, type Goal } from '../api.ts';

/**
 * Goals: the workflows you have in progress, and how far through they are.
 *
 * The fleet board is a table of runs, which is the right shape for an operator
 * scanning a deployment and the wrong one for the question people actually
 * arrive with — "where has my thing got to". So this is one card per run rather
 * than one row: the workflow, the stage it is on now, a bar, and the cost so
 * far, at a size you can read across a desk.
 *
 * Two honesty rules run through it, both inherited from the query behind it.
 * The denominator is the workflow as committed *now* while a run pins the
 * revision it started against, so a workflow edited mid-run moves its own
 * goalposts; and child steps are counted on neither side of the fraction. The
 * bar is therefore a fair reading of a run's shape, not an accounting of its
 * work, and the page says so once at the bottom rather than pretending to a
 * precision it has not got.
 */
export function GoalsView() {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [hidden, setHidden] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(() => {
    api
      .goals()
      .then((result) => {
        setGoals(result.goals);
        setHidden(result.hidden);
        setError(null);
      })
      .catch((caught) => setError((caught as Error).message))
      .finally(() => setLoaded(true));
  }, []);

  useEffect(refresh, [refresh]);

  /*
   * Polled rather than streamed. A stream per run would be the accurate way to
   * do this and the wrong trade at this size: the board is watching for a step
   * to finish, which happens on the order of minutes, and it would hold one
   * connection per card open to catch it a few seconds earlier. The run
   * timeline is where live actually earns its cost.
   */
  useEffect(() => {
    const timer = setInterval(refresh, 15_000);
    return () => clearInterval(timer);
  }, [refresh]);

  const waiting = goals.filter((goal) => goal.awaitingApproval).length;

  return (
    <div className="studio">
      <header className="studio-head">
        <span className="studio-crumb">
          <span className="studio-crumb-root">Goals</span>
          <span className="studio-crumb-sep">/</span>
          <span>in progress</span>
        </span>
        <span className="spacer" />
        {waiting > 0 && <span className="chip warn">{waiting} waiting on a person</span>}
      </header>

      <div className="studio-column wide">
        {error && (
          <div className="banner danger">
            <p style={{ margin: 0 }}>{error}</p>
          </div>
        )}

        {loaded && goals.length === 0 && !error && (
          <section className="studio-card">
            <div className="studio-card-head">
              <h2>Nothing in flight</h2>
            </div>
            <div className="studio-card-body">
              <p style={{ margin: 0 }}>
                None of the workflows you can see has a run in progress. Start one from the builder
                and it will appear here while it works.
              </p>
              {/*
                An empty list and an empty *board* look identical, and one of
                them is a bug. Saying it in a sentence is what tells somebody
                the page loaded and the answer is genuinely nought — otherwise
                the reasonable response to a blank column is to reload it.
              */}
            </div>
          </section>
        )}

        {goals.map((goal) => (
          <GoalCard key={goal.runId} goal={goal} />
        ))}

        {goals.length > 0 && (
          <p className="hint">
            Progress counts top-level steps finished against the steps the workflow has today. A
            workflow edited while one of its runs is going therefore moves its own goalposts, and
            steps a step spawns for itself are counted on neither side.
            {hidden > 0 && ` ${hidden} more ${hidden === 1 ? 'run is' : 'runs are'} in flight on workflows you have not been shared on.`}
          </p>
        )}
      </div>
    </div>
  );
}

function GoalCard({ goal }: { goal: Goal }) {
  const { fraction } = goal;
  const percent = fraction === null ? 0 : Math.round(fraction * 100);

  return (
    <section className="studio-card">
      <div className="studio-card-head">
        <h2>{goal.workflowName}</h2>
        <span className="spacer" />
        <span className="studio-row">
          {/*
            Waiting is the first thing on the card and it is not a progress
            state. A run held for approval is not slow, it is stopped, and the
            only person who can move it is reading this — so it gets a chip
            where the eye lands rather than a line further down.
          */}
          {goal.awaitingApproval && <span className="chip warn">waiting on a person</span>}
          {goal.failedSteps > 0 && (
            <span className="chip warn">
              {goal.failedSteps} step{goal.failedSteps === 1 ? '' : 's'} failed
            </span>
          )}
          {goal.beyondPlan && <span className="chip warn">edited mid-run</span>}
          <span className="chip">{goal.status.replace(/_/g, ' ')}</span>
        </span>
      </div>

      <div className="studio-card-body">
        <p style={{ margin: 0 }}>
          <span className="hint">Stage</span>
          <br />
          <strong>{stage(goal)}</strong>
        </p>

        {fraction !== null ? (
          <div
            className={`goal-bar${goal.awaitingApproval ? ' waiting' : ''}`}
            role="progressbar"
            aria-valuenow={goal.completedSteps}
            aria-valuemin={0}
            aria-valuemax={goal.totalSteps}
            aria-valuetext={`${goal.completedSteps} of ${goal.totalSteps} steps finished`}
            aria-label={`Progress through ${goal.workflowName}`}
          >
            {/*
              Width, and only width, carries this — which is why the bar has an
              explicit `aria-valuetext` and the same fraction is written out
              beside it. A reader who cannot see the fill still gets the number,
              and so does a reader who can but does not trust it.
            */}
            <span className="goal-bar-fill" style={{ width: `${percent}%` }} />
          </div>
        ) : (
          /*
            No denominator, so no bar. The workflow has been deleted out from
            under its run, or has no top-level steps left. A bar drawn from a
            division by zero is worse than none: it would show a confident nought
            per cent for a run that may be nearly done.
          */
          <div className="goal-bar unmeasured" aria-hidden="true" />
        )}

        <p className="tile-note" style={{ marginTop: 0 }}>
          {fraction !== null
            ? `${goal.completedSteps} of ${goal.totalSteps} steps · ${percent}%`
            : 'No step list to measure against — this workflow has been changed or removed since the run began.'}
          {goal.beyondPlan &&
            ' · more steps have finished than the workflow now has, because it was edited while this run was going.'}
        </p>

        {goal.awaitingApproval && (
          <p className="tile-note" style={{ marginTop: 0 }}>
            This run is not progressing. It is waiting for somebody to approve or revise a step —
            the Review Queue is where that decision is made.
          </p>
        )}

        {goal.failedSteps > 0 && (
          <p className="tile-note" style={{ marginTop: 0 }}>
            {goal.failedSteps === 1 ? 'A step has failed' : `${goal.failedSteps} steps have failed`}{' '}
            and the run is carrying on. The run timeline has the error.
          </p>
        )}

        <p className="tile-note" style={{ marginTop: 0 }}>
          Started {goal.triggeredBy ? `by ${goal.triggeredBy}` : `by ${goal.triggerType}`} ·{' '}
          {new Date(goal.createdAt).toLocaleString()} · ${(goal.spendCents / 100).toFixed(2)} so far
          · revision {goal.revision} · <code>{goal.runId}</code>
        </p>
      </div>
    </section>
  );
}

/**
 * What to call the stage a run is on.
 *
 * The three cases are genuinely different and only the first is the happy one.
 * A step has a name and that is what a person recognises; a step with no name
 * still has an id, which at least identifies it; and a run *between* steps has
 * no current step at all — one has finished and the next has not been picked
 * up. Rendering an em dash for the last two would say "unknown" about a state
 * that is perfectly well known, so each says what it means.
 */
function stage(goal: Goal): string {
  if (goal.currentStepName) return goal.currentStepName;
  if (goal.currentStep) return goal.currentStep;
  if (goal.awaitingApproval) return 'Held for approval, between steps';
  if (goal.status === 'paused') return 'Paused between steps';
  return 'Between steps — nothing is running this moment';
}
